/**
 * Profil & Einstellungen - Dynamische Kundendaten aus Auth-Context
 * Design: Sanctuary / Gold-Olive Akzent (#C9A96E)
 */
import { sharedConfig } from "@/config/clientConfig";
import { RestartTourButton } from "@/components/OnboardingTour";
import NotificationPreferencesSection from "@/components/NotificationPreferencesSection";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  User,
  Phone,
  Heart,
  Shield,
  Sparkles,
  Loader2,
  Send as SendIcon,
  CheckCircle2,
  Unlink,
  MessageSquare,
  CalendarDays,
  Pencil,
  Star,
  X,
  Volume2,
  Bell,
  Clock,
  CreditCard,
  Music,
  Crown,
  ExternalLink,
  Copy,
  Users,
  Smartphone,
  Camera,
  Trash2,
  ImagePlus,
  Languages,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/app/LanguageSwitcher";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

import { createLogger } from "@/lib/logger";
const log = createLogger("Appprofil");

/** Formatiert YYYY-MM-DD als "15. März 1990" */
function formatBirthdate(dateStr: string): string {
  const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateStr;
  const date = new Date(
    parseInt(match[1]),
    parseInt(match[2]) - 1,
    parseInt(match[3])
  );
  return date.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Sternzeichen aus YYYY-MM-DD berechnen */
function getZodiacFromDate(
  dateStr: string
): { name: string; symbol: string } | null {
  const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const month = parseInt(match[2]);
  const day = parseInt(match[3]);

  const signs: {
    name: string;
    symbol: string;
    from: [number, number];
    to: [number, number];
  }[] = [
    { name: "Steinbock", symbol: "♑", from: [12, 22], to: [1, 19] },
    { name: "Wassermann", symbol: "♒", from: [1, 20], to: [2, 18] },
    { name: "Fische", symbol: "♓", from: [2, 19], to: [3, 20] },
    { name: "Widder", symbol: "♈", from: [3, 21], to: [4, 19] },
    { name: "Stier", symbol: "♉", from: [4, 20], to: [5, 20] },
    { name: "Zwillinge", symbol: "♊", from: [5, 21], to: [6, 20] },
    { name: "Krebs", symbol: "♋", from: [6, 21], to: [7, 22] },
    { name: "Löwe", symbol: "♌", from: [7, 23], to: [8, 22] },
    { name: "Jungfrau", symbol: "♍", from: [8, 23], to: [9, 22] },
    { name: "Waage", symbol: "♎", from: [9, 23], to: [10, 22] },
    { name: "Skorpion", symbol: "♏", from: [10, 23], to: [11, 21] },
    { name: "Schütze", symbol: "♐", from: [11, 22], to: [12, 21] },
  ];

  for (const sign of signs) {
    const [fromMonth, fromDay] = sign.from;
    const [toMonth, toDay] = sign.to;
    if (fromMonth > toMonth) {
      if (
        (month === fromMonth && day >= fromDay) ||
        (month === toMonth && day <= toDay)
      ) {
        return { name: sign.name, symbol: sign.symbol };
      }
    } else {
      if (
        (month === fromMonth && day >= fromDay) ||
        (month === toMonth && day <= toDay) ||
        (month > fromMonth && month < toMonth)
      ) {
        return { name: sign.name, symbol: sign.symbol };
      }
    }
  }
  return null;
}

function BirthdateSection({ clientId }: { clientId: string }) {
  const [editing, setEditing] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const birthdateQuery = trpc.chat.getBirthdate.useQuery(
    { clientId },
    { enabled: !!clientId }
  );
  const saveMutation = trpc.chat.saveBirthdate.useMutation();

  const birthdate = birthdateQuery.data?.birthdate;
  const zodiac = birthdate ? getZodiacFromDate(birthdate) : null;

  const handleSave = async () => {
    if (!dateInput) return;
    setSaving(true);
    try {
      await saveMutation.mutateAsync({ clientId, birthdate: dateInput });
      birthdateQuery.refetch();
      setEditing(false);
      setDateInput("");
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (err) {
      log.error("Fehler beim Speichern:");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setDateInput(birthdate || "");
    setEditing(true);
  };

  if (birthdateQuery.isLoading) {
    return (
      <div className="px-5 py-3.5 flex items-center justify-between">
        <span className="text-sm text-[#2D2D2D]/50">Geburtsdatum</span>
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2D2D2D]/20" />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="px-5 py-3.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[#2D2D2D]/50">Geburtsdatum</span>
          <button
            onClick={() => {
              setEditing(false);
              setDateInput("");
            }}
            className="p-1 rounded-lg text-[#2D2D2D]/30 hover:text-[#2D2D2D]/60 transition-all"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex gap-2">
          <input
            type="date"
            value={dateInput}
            onChange={e => setDateInput(e.target.value)}
            max={new Date().toISOString().split("T")[0]}
            className="flex-1 bg-[#F5F0EB] rounded-xl px-3 py-2 text-sm text-[#2D2D2D] outline-none focus:ring-2 focus:ring-[#C9A96E]/20 transition-all"
          />
          <button
            onClick={handleSave}
            disabled={!dateInput || saving}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-1.5 ${
              dateInput && !saving
                ? "bg-[#C9A96E] text-white hover:bg-[#B89A5E] shadow-sm"
                : "bg-[#2D2D2D]/[0.03] text-[#2D2D2D]/20"
            }`}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            Speichern
          </button>
        </div>
        <p className="text-[10px] text-[#2D2D2D]/25 mt-1.5">
          Wird nur für dein Tageshoroskop verwendet.
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-3.5 flex items-center justify-between">
      <span className="text-sm text-[#2D2D2D]/50">Geburtsdatum</span>
      <div className="flex items-center gap-2">
        {birthdate ? (
          <>
            <div className="flex items-center gap-1.5">
              {zodiac && (
                <span className="text-base" title={zodiac.name}>
                  {zodiac.symbol}
                </span>
              )}
              <span className="text-sm text-[#2D2D2D] font-medium">
                {formatBirthdate(birthdate)}
              </span>
              {zodiac && (
                <span className="text-[10px] text-[#C9A96E] bg-[#C9A96E]/10 px-1.5 py-0.5 rounded-full font-medium">
                  {zodiac.name}
                </span>
              )}
            </div>
            <button
              onClick={startEditing}
              className="p-1 rounded-lg text-[#2D2D2D]/20 hover:text-[#C9A96E] hover:bg-[#C9A96E]/5 transition-all"
              title="Geburtsdatum ändern"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            {justSaved ? (
              <span className="text-sm text-[#C9A96E] flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Gespeichert
              </span>
            ) : (
              <button
                onClick={startEditing}
                className="text-sm text-[#C9A96E] hover:text-[#B89A5E] font-medium flex items-center gap-1.5 transition-all"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Hinzufügen
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Check-in-Einstellungen: Toggle + Uhrzeit für tägliche Telegram-Erinnerungen.
 */
function NotificationSettings({
  clientId,
  linkData,
}: {
  clientId: string;
  linkData: {
    checkinsEnabled: boolean;
    checkinTime: string;
    eveningReminderEnabled: boolean;
    eveningReminderTime: string;
  };
}) {
  const [morningEnabled, setMorningEnabled] = useState(
    linkData.checkinsEnabled
  );
  const [morningTime, setMorningTime] = useState(
    linkData.checkinTime || "08:00"
  );
  const [eveningEnabled, setEveningEnabled] = useState(
    linkData.eveningReminderEnabled
  );
  const [eveningTime, setEveningTime] = useState(
    linkData.eveningReminderTime || "21:00"
  );
  const [saving, setSaving] = useState(false);
  const utils = trpc.useUtils();

  const updateMutation = trpc.telegram.updateCheckinSettings.useMutation({
    onSuccess: () => {
      utils.telegram.getLink.invalidate({ clientId });
      setSaving(false);
    },
    onError: () => setSaving(false),
  });

  const ToggleSwitch = ({
    enabled,
    onToggle,
  }: {
    enabled: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className={`relative w-9 h-5 rounded-full transition-colors ${enabled ? "bg-[#C9A96E]" : "bg-[#2D2D2D]/15"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${enabled ? "translate-x-4" : "translate-x-0"}`}
      />
    </button>
  );

  const TimeSelect = ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (v: string) => void;
  }) => (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="text-xs bg-white rounded-lg px-2 py-1 text-[#2D2D2D] border border-[#2D2D2D]/10 outline-none"
    >
      {Array.from({ length: 24 }, (_, h) => {
        const hh = String(h).padStart(2, "0");
        return <option key={hh} value={`${hh}:00`}>{`${hh}:00`}</option>;
      })}
    </select>
  );

  return (
    <div className="bg-[#F5F0EB] rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 pb-1">
        <Bell className="w-4 h-4 text-[#C9A96E]" />
        <span className="text-sm font-semibold text-[#2D2D2D]">
          Benachrichtigungen
        </span>
        {saving && (
          <Loader2 className="w-3 h-3 animate-spin text-[#C9A96E] ml-auto" />
        )}
      </div>

      {/* Morgen Check-in */}
      <div className="bg-white rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[#2D2D2D]">
              Morgen Check-in
            </p>
            <p className="text-[11px] text-[#2D2D2D]/40">
              Tägliche Nachricht von Annam
            </p>
          </div>
          <ToggleSwitch
            enabled={morningEnabled}
            onToggle={() => {
              const newVal = !morningEnabled;
              setMorningEnabled(newVal);
              setSaving(true);
              updateMutation.mutate({ clientId, checkinsEnabled: newVal });
            }}
          />
        </div>
        {morningEnabled && (
          <div className="flex items-center gap-2 pt-1">
            <Clock className="w-3 h-3 text-[#2D2D2D]/40" />
            <span className="text-[11px] text-[#2D2D2D]/40">Uhrzeit:</span>
            <TimeSelect
              value={morningTime}
              onChange={newTime => {
                setMorningTime(newTime);
                setSaving(true);
                updateMutation.mutate({ clientId, checkinTime: newTime });
              }}
            />
          </div>
        )}
      </div>

      {/* Abend Tagebuch-Erinnerung */}
      <div className="bg-white rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[#2D2D2D]">
              Tagebuch-Erinnerung
            </p>
            <p className="text-[11px] text-[#2D2D2D]/40">
              Abendliche Reflexion mit Tagebuch-Link
            </p>
          </div>
          <ToggleSwitch
            enabled={eveningEnabled}
            onToggle={() => {
              const newVal = !eveningEnabled;
              setEveningEnabled(newVal);
              setSaving(true);
              updateMutation.mutate({
                clientId,
                eveningReminderEnabled: newVal,
              });
            }}
          />
        </div>
        {eveningEnabled && (
          <div className="flex items-center gap-2 pt-1">
            <Clock className="w-3 h-3 text-[#2D2D2D]/40" />
            <span className="text-[11px] text-[#2D2D2D]/40">Uhrzeit:</span>
            <TimeSelect
              value={eveningTime}
              onChange={newTime => {
                setEveningTime(newTime);
                setSaving(true);
                updateMutation.mutate({
                  clientId,
                  eveningReminderTime: newTime,
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TelegramLinkSection({ clientId }: { clientId: string }) {
  const [step, setStep] = useState<"idle" | "code">("idle");
  const [linkCode, setLinkCode] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, refetch } = trpc.telegram.getLink.useQuery({
    clientId,
  });
  const unlinkMutation = trpc.telegram.unlink.useMutation();
  const linkMutation = trpc.telegram.link.useMutation();

  const BOT_USERNAME = "@Ayu_health_bot";
  const BOT_URL = `https://t.me/Ayu_health_bot?start=${clientId}`;

  const handleUnlink = async () => {
    if (!data?.telegramChatId) return;
    toast.promise(unlinkMutation.mutateAsync({ clientId }), {
      loading: "Verknüpfung wird aufgehoben...",
      success: () => {
        refetch();
        setStep("idle");
        setLinkCode("");
        setLinkError(null);
        return "Telegram-Konto entkoppelt.";
      },
      error: "Fehler beim Entkoppeln.",
    });
  };

  const startLinking = () => {
    window.open(BOT_URL, "_blank", "noopener,noreferrer");
    setStep("code");
    setLinkError(null);
  };

  const copyBotName = () => {
    navigator.clipboard.writeText(BOT_USERNAME);
    setCopied(true);
    toast.success("Bot-Name kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmitCode = async () => {
    if (!linkCode.trim()) return;
    setLinkError(null);
    try {
      const result = await linkMutation.mutateAsync({
        clientId,
        code: linkCode.trim(),
      });
      if (result.success) {
        toast.success("Telegram erfolgreich verknüpft!");
        refetch();
        setStep("idle");
        setLinkCode("");
      } else {
        setLinkError(
          result.error || "Code ungültig. Bitte versuche es erneut."
        );
      }
    } catch (err: any) {
      setLinkError(err?.message || "Fehler bei der Verknüpfung.");
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 p-5 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#C9A96E]" />
        <span className="text-sm text-[#2D2D2D]/40">
          Lade Telegram-Status...
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <SendIcon className="w-4 h-4 text-[#C9A96E]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">
          Annam — Telegram
        </h2>
      </div>
      <div className="p-5 space-y-4">
        {data?.telegramChatId ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C9A96E]/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#C9A96E]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#2D2D2D]">
                  Erfolgreich verknüpft
                </p>
                <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                  Annam ist über Telegram erreichbar.
                </p>
              </div>
              <button
                onClick={handleUnlink}
                className="ml-auto p-2 rounded-lg text-[#2D2D2D]/30 hover:text-red-500 hover:bg-red-500/5 transition-colors"
                title="Verknüpfung aufheben"
              >
                <Unlink className="w-4 h-4" />
              </button>
            </div>
            {data.checkinsEnabled !== undefined &&
              data.checkinTime !== undefined && (
                <NotificationSettings
                  clientId={clientId}
                  linkData={data as any}
                />
              )}
          </>
        ) : step === "code" ? (
          <>
            <div className="bg-[#f0f7ff] rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-[#2D2D2D]">So geht’s:</p>
              <ol className="text-sm text-[#2D2D2D]/70 space-y-2 list-decimal list-inside">
                <li>
                  Öffne <strong>Telegram</strong> auf deinem Handy oder Desktop
                </li>
                <li>
                  Suche nach{" "}
                  <button
                    onClick={copyBotName}
                    className="inline-flex items-center gap-1 font-mono font-bold text-[#0088cc] hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    {BOT_USERNAME}{" "}
                    {copied ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </li>
                <li>
                  Klicke auf <strong>„START“</strong> — der Bot sendet dir einen{" "}
                  <strong>Verknüpfungscode</strong>
                </li>
                <li>Gib den Code hier unten ein</li>
              </ol>
            </div>

            <div className="flex items-center gap-2 p-3 bg-[#C9A96E]/5 rounded-xl">
              <Smartphone className="w-4 h-4 text-[#C9A96E] shrink-0" />
              <p className="text-xs text-[#2D2D2D]/50">
                Funktioniert am Handy und am Desktop — du brauchst nur die
                Telegram-App.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#2D2D2D]/60">
                Verknüpfungscode
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={linkCode}
                  onChange={e => setLinkCode(e.target.value.toUpperCase())}
                  placeholder="z.B. A3X7K2"
                  maxLength={10}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-[#2D2D2D]/10 text-sm text-center font-mono tracking-widest text-[#2D2D2D] placeholder:text-[#2D2D2D]/20 focus:outline-none focus:ring-2 focus:ring-[#0088cc]/30 bg-white"
                  onKeyDown={e => e.key === "Enter" && handleSubmitCode()}
                />
                <button
                  onClick={handleSubmitCode}
                  disabled={!linkCode.trim() || linkMutation.isPending}
                  className="px-4 py-2.5 rounded-xl bg-[#0088cc] text-white font-medium hover:bg-[#0077b3] transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                >
                  {linkMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Verbinden
                </button>
              </div>
              {linkError && (
                <p className="text-xs text-red-500 mt-1">{linkError}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={BOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#0088cc] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Bot direkt öffnen
              </a>
              <span className="text-[#2D2D2D]/20">|</span>
              <button
                onClick={() => {
                  setStep("idle");
                  setLinkCode("");
                  setLinkError(null);
                }}
                className="text-xs text-[#2D2D2D]/40 hover:text-[#2D2D2D]/60"
              >
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-[#2D2D2D]/50">
              Verbinde dein Konto mit Annam auf Telegram
              für tägliche Check-ins und Updates.
            </p>
            <button
              onClick={startLinking}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0088cc] text-white font-medium hover:bg-[#0077b3] transition-colors shadow-sm"
            >
              <SendIcon className="w-4 h-4" />
              Mit Annam verbinden
            </button>
            <p className="text-xs text-[#2D2D2D]/40 text-center">
              Suche in Telegram nach{" "}
              <strong className="font-mono">{BOT_USERNAME}</strong> oder klicke
              oben.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** Community Bot Verknüpfung */
function CommunityBotSection({ clientId }: { clientId: string }) {
  const { data: linkData } = trpc.telegram.getLink.useQuery({ clientId });
  const isCoachLinked = !!linkData?.telegramChatId;

  const COMMUNITY_BOT = "@Ayu_community_bot";
  const COMMUNITY_URL = `https://t.me/Ayu_community_bot?start=ref_${clientId}`;

  const [copied, setCopied] = useState(false);

  const copyBotName = () => {
    navigator.clipboard.writeText(COMMUNITY_BOT);
    setCopied(true);
    toast.success("Bot-Name kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <Users className="w-4 h-4 text-[#6b7f4e]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">
          AYU Community — Telegram
        </h2>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-[#2D2D2D]/50 mb-1">
          Erhalte Community-Nachrichten auch auf Telegram und tausche dich
          unterwegs mit anderen Mitgliedern aus.
        </p>

        {/* Step-by-Step Anleitung */}
        <div className="bg-[#f0f7f0] rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-[#2d3a1e] uppercase tracking-wider">
            So trittst du bei — 2 einfache Schritte:
          </p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                1
              </span>
              <p className="text-sm text-[#5a6b4a]">
                Klicke auf den Button unten — er öffnet den{" "}
                <strong>AYU Community Bot</strong> direkt in Telegram.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                2
              </span>
              <p className="text-sm text-[#5a6b4a]">
                Tippe auf{" "}
                <span className="font-mono bg-white px-1.5 py-0.5 rounded text-[#2d3a1e] font-medium">
                  START
                </span>{" "}
                — dein Account wird <strong>automatisch verknüpft</strong>.
                Fertig!
              </p>
            </div>
          </div>
        </div>

        <a
          href={COMMUNITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#6b7f4e] text-white font-medium hover:bg-[#5a6b3e] transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
          </svg>
          Community Bot in Telegram öffnen
        </a>

        <p className="text-xs text-[#2D2D2D]/40 text-center">
          Falls der Link nicht funktioniert: Suche in Telegram nach{" "}
          <button
            onClick={copyBotName}
            className="inline-flex items-center gap-1 font-mono font-medium text-[#6b7f4e] hover:underline cursor-pointer bg-transparent border-none p-0"
          >
            {COMMUNITY_BOT}{" "}
            {copied ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </p>

        <div className="flex items-center gap-2 p-3 bg-[#6b7f4e]/5 rounded-xl">
          <Smartphone className="w-4 h-4 text-[#6b7f4e] shrink-0" />
          <p className="text-xs text-[#2D2D2D]/50">
            Funktioniert am Handy und am Desktop — du brauchst nur die
            Telegram-App.
          </p>
        </div>
      </div>
    </div>
  );
}

const COACHING_STYLES = [
  {
    value: "supportive",
    label: "Unterstützend",
    desc: "Empathisch & ermutigend",
  },
  { value: "direct", label: "Direkt", desc: "Klar & auf den Punkt" },
  {
    value: "analytical",
    label: "Analytisch",
    desc: "Datenbasiert & strukturiert",
  },
  { value: "mindful", label: "Achtsam", desc: "Präsent & reflektierend" },
];

function CoachingStyleSection({
  clientId,
  currentStyle,
}: {
  clientId: string;
  currentStyle: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(currentStyle);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { refetch } = useClientAuth();

  const saveStyle = async (style: string) => {
    setSaving(true);
    try {
      // Raw fetch to avoid tRPC dependency here, simplifies cookie handling
      await fetch("/api/trpc/clientAuth.saveCoachingStyle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ json: { clientId, coachingStyle: style } }),
      });
      setSelected(style);
      setSaved(true);
      refetch();
      setTimeout(() => {
        setSaved(false);
        setEditing(false);
      }, 1200);
    } catch (e) {
      log.error("Coaching-Stil speichern fehlgeschlagen:");
    } finally {
      setSaving(false);
    }
  };

  const currentLabel =
    COACHING_STYLES.find(s => s.value === selected)?.label || selected;

  if (!editing) {
    return (
      <div className="px-5 py-3.5 flex items-center justify-between">
        <span className="text-sm text-[#2D2D2D]/50">Coaching-Stil</span>
        <button
          onClick={() => setEditing(true)}
          className="flex items-center gap-1.5 text-sm text-[#B87A5E] font-medium bg-[#B87A5E]/5 px-2.5 py-0.5 rounded-full hover:bg-[#B87A5E]/10 transition-colors"
        >
          {currentLabel || "Festlegen"}
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="px-5 py-3.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#2D2D2D]/50">Coaching-Stil wählen</span>
        <button
          onClick={() => setEditing(false)}
          className="text-[#2D2D2D]/30 hover:text-[#2D2D2D]/60"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid gap-1.5">
        {COACHING_STYLES.map(style => (
          <button
            key={style.value}
            onClick={() => saveStyle(style.value)}
            disabled={saving}
            className={`text-left px-3 py-2 rounded-lg border transition-all duration-200 ${
              selected === style.value
                ? "border-[#B87A5E] bg-[#B87A5E]/5"
                : "border-[#2D2D2D]/5 hover:border-[#B87A5E]/30 hover:bg-[#B87A5E]/3"
            }`}
          >
            <span className="text-sm font-medium text-[#2D2D2D]">
              {style.label}
            </span>
            <span className="text-xs text-[#2D2D2D]/40 ml-2">{style.desc}</span>
            {saved && selected === style.value && (
              <CheckCircle2 className="w-3.5 h-3.5 text-[#C9A96E] inline ml-2" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function LanguageSection() {
  const { locale } = useI18n();

  const labelMap: Record<string, string> = {
    de: "Deutsch / German",
    en: "English / Englisch",
  };

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <Languages className="w-4 h-4 text-[#C9A96E]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">Sprache / Language</h2>
        <span className="ml-auto text-xs text-[#2D2D2D]/40">{labelMap[locale]}</span>
      </div>
      <div className="p-5">
        <p className="text-xs text-[#2D2D2D]/40 mb-4">
          Wähle deine bevorzugte Sprache für die App. / Choose your preferred language for the app.
        </p>
        <LanguageSwitcher />
      </div>
    </div>
  );
}

type VoicePref = "annam" | "christopher";

function VoicePreferenceSection({
  clientId,
  currentPreference,
}: {
  clientId: string;
  currentPreference: VoicePref;
}) {
  const { client, refetch } = useClientAuth();
  const [preference, setPreference] = useState<VoicePref>(currentPreference);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const saveMutation = trpc.clientAuth.saveVoicePreference.useMutation({
    onSuccess: () => {
      setSaved(true);
      refetch();
      setTimeout(() => setSaved(false), 2000);
    },
    onSettled: () => setSaving(false),
  });

  const handleChange = (newPref: VoicePref) => {
    if (newPref === preference) return;
    setPreference(newPref);
    setSaving(true);
    saveMutation.mutate({ clientId, voicePreference: newPref });
  };

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <Volume2 className="w-4 h-4 text-[#C9A96E]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">Stimme wählen</h2>
        {saved && (
          <span className="ml-auto flex items-center gap-1 text-xs text-[#C9A96E] animate-in fade-in duration-300">
            <CheckCircle2 className="w-3 h-3" /> Gespeichert
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="text-xs text-[#2D2D2D]/40 mb-4">
          Wähle, wer dich als Coach begleitet und mit welcher Stimme.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Annam */}
          <button
            onClick={() => handleChange("annam")}
            disabled={saving}
            className={`relative flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all duration-200 ${
              preference === "annam"
                ? "border-[#C9A96E] bg-[#C9A96E]/5 shadow-sm"
                : "border-[#2D2D2D]/10 hover:border-[#2D2D2D]/20 bg-white"
            }`}
          >
            {preference === "annam" && (
              <div className="absolute top-2 right-2">
                <CheckCircle2 className="w-4 h-4 text-[#C9A96E]" />
              </div>
            )}
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                preference === "annam" ? "bg-[#C9A96E]/10" : "bg-[#2D2D2D]/5"
              }`}
            >
              <Sparkles
                className={`w-5 h-5 ${preference === "annam" ? "text-[#C9A96E]" : "text-[#2D2D2D]/40"}`}
              />
            </div>
            <div className="text-center">
              <p
                className={`text-sm font-medium ${preference === "annam" ? "text-[#C9A96E]" : "text-[#2D2D2D]"}`}
              >
                Annam
              </p>
              <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                ♀ Warm • Einfühlsam
              </p>
            </div>
          </button>

          {/* Christopher */}
          <button
            onClick={() => handleChange("christopher")}
            disabled={saving}
            className={`relative flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all duration-200 ${
              preference === "christopher"
                ? "border-[#C9A96E] bg-[#C9A96E]/5 shadow-sm"
                : "border-[#2D2D2D]/10 hover:border-[#2D2D2D]/20 bg-white"
            }`}
          >
            {preference === "christopher" && (
              <div className="absolute top-2 right-2">
                <CheckCircle2 className="w-4 h-4 text-[#C9A96E]" />
              </div>
            )}
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                preference === "christopher"
                  ? "bg-[#C9A96E]/10"
                  : "bg-[#2D2D2D]/5"
              }`}
            >
              <Sparkles
                className={`w-5 h-5 ${preference === "christopher" ? "text-[#C9A96E]" : "text-[#2D2D2D]/40"}`}
              />
            </div>
            <div className="text-center">
              <p
                className={`text-sm font-medium ${preference === "christopher" ? "text-[#C9A96E]" : "text-[#2D2D2D]"}`}
              >
                Christopher
              </p>
              <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                ♂ Strukturiert • Direkt
              </p>
            </div>
          </button>
        </div>
        {saving && (
          <div className="flex items-center gap-2 mt-3 text-xs text-[#2D2D2D]/40">
            <Loader2 className="w-3 h-3 animate-spin" /> Wird gespeichert...
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Subscription Management Section ─── */
function SubscriptionSection({
  clientId,
  email,
}: {
  clientId: string;
  email: string;
}) {
  const { isDemo } = useDemo();

  // Im Demo-Modus: Keine Preise/Checkout, stattdessen CTA zu /termin
  if (isDemo) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-[#d4dcc4] shadow-sm">
        <h3 className="text-lg font-semibold text-[#2d3a1e] mb-3">
          Abonnement
        </h3>
        <p className="text-sm text-[#5a6b4a] mb-4">
          Im Demo-Modus sind keine Zahlungsfunktionen verfuegbar.
        </p>
        <a
          href="/termin"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#C9A96E] text-white text-sm font-medium hover:bg-[#b8953d] transition-colors"
        >
          Kostenlose Beratung buchen
        </a>
      </div>
    );
  }
  const { data: premiumStatus, isLoading } =
    trpc.stripe.getPremiumStatus.useQuery({ clientId });
  const createCheckout = trpc.stripe.createCheckout.useMutation();
  const createPortal = trpc.stripe.createBillingPortal.useMutation();

  const handleUpgrade = async (planId: string) => {
    try {
      const result = await createCheckout.mutateAsync({
        planId,
        clientId,
        clientEmail: email,
        origin: window.location.origin,
      });
      if (result.url) {
        toast("Weiterleitung zu Stripe...", {
          description: "Du wirst zum Checkout weitergeleitet.",
        });
        window.open(result.url, "_blank");
      }
    } catch (e: any) {
      toast.error("Fehler", {
        description: e.message || "Checkout konnte nicht gestartet werden.",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await createPortal.mutateAsync({
        clientId,
        origin: window.location.origin,
      });
      if (result.url) {
        window.open(result.url, "_blank");
      }
    } catch (e: any) {
      toast.error("Fehler", {
        description:
          e.message || "Abo-Verwaltung konnte nicht geöffnet werden.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 p-5 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#C9A96E]" />
        <span className="text-sm text-[#2D2D2D]/40">Lade Abo-Status...</span>
      </div>
    );
  }

  const planLabels: Record<string, string> = {
    starter: "Starter (10 Sessions/Monat)",
    medium: "Medium (25 Sessions/Monat)",
    unlimited: "Unlimited (unbegrenzt)",
    admin_grant: "Premium (Geschenk)",
    premium_package: "Premium-Paket (unbegrenzt)",
  };

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <Music className="w-4 h-4 text-[#C9A96E]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">
          Audio-Sessions Abo
        </h2>
      </div>
      <div className="p-5 space-y-4">
        {premiumStatus?.hasAccess && premiumStatus.plan ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C9A96E]/10 flex items-center justify-center">
                <Crown className="w-5 h-5 text-[#C9A96E]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#C9A96E]">
                  {planLabels[premiumStatus.plan] || premiumStatus.plan}
                </p>
                {premiumStatus.sessionsLimit !== null && (
                  <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                    {premiumStatus.sessionsUsed} / {premiumStatus.sessionsLimit}{" "}
                    Sessions diesen Monat genutzt
                  </p>
                )}
                {premiumStatus.sessionsLimit === null &&
                  premiumStatus.plan !== "admin_grant" &&
                  premiumStatus.plan !== "premium_package" && (
                    <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                      Unbegrenzte Sessions
                    </p>
                  )}
              </div>
            </div>
            {premiumStatus.plan !== "admin_grant" &&
              premiumStatus.hasStripeCustomer && (
                <button
                  onClick={handleManageSubscription}
                  disabled={createPortal.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#2D2D2D]/10 text-sm text-[#2D2D2D]/60 hover:bg-[#2D2D2D]/[0.02] transition-colors"
                >
                  {createPortal.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Abo verwalten
                      <ExternalLink className="w-3 h-3" />
                    </>
                  )}
                </button>
              )}
            {premiumStatus.plan !== "admin_grant" &&
              !premiumStatus.hasStripeCustomer && (
                <p className="text-xs text-center text-[#2D2D2D]/40">
                  Manuell freigeschaltet — kein Stripe-Abo hinterlegt
                </p>
              )}
          </>
        ) : (
          <>
            <p className="text-sm text-[#2D2D2D]/50">
              Schalte personalisierte Premium Audio-Sessions frei — Atemübungen,
              Meditationen, Körperreisen und mehr.
            </p>
            <div className="grid gap-3">
              {[
                {
                  id: "starter",
                  label: "Starter",
                  price: "9,90",
                  sessions: "10 Sessions/Monat",
                },
                {
                  id: "medium",
                  label: "Medium",
                  price: "19,90",
                  sessions: "25 Sessions/Monat",
                  popular: true,
                },
                {
                  id: "unlimited",
                  label: "Unlimited",
                  price: "29,90",
                  sessions: "Unbegrenzt",
                },
              ].map(plan => (
                <button
                  key={plan.id}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={createCheckout.isPending}
                  className={`relative text-left p-4 rounded-xl border transition-all ${
                    plan.popular
                      ? "border-[#C9A96E]/30 bg-[#C9A96E]/[0.02] hover:border-[#C9A96E]/50"
                      : "border-[#2D2D2D]/10 hover:border-[#2D2D2D]/20"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-2.5 right-3 bg-[#C9A96E] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      Beliebt
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#2D2D2D]">
                        {plan.label}
                      </p>
                      <p className="text-xs text-[#2D2D2D]/40 mt-0.5">
                        {plan.sessions}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#C9A96E]">
                        {plan.price} €
                      </p>
                      <p className="text-[10px] text-[#2D2D2D]/30">pro Monat</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {premiumStatus && premiumStatus.purchasedSessions.length > 0 && (
              <p className="text-xs text-[#2D2D2D]/40">
                Du hast {premiumStatus.purchasedSessions.length}{" "}
                Einzelsession(s) freigeschaltet.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AppProfil() {
  const { client, loading, refetch } = useClientAuth();
  const [avatarUploading, setAvatarUploading] = useState(false);

  const uploadAvatarMutation = trpc.clientAuth.uploadAvatar.useMutation({
    onSuccess: data => {
      if (data.success) {
        toast.success("Profilbild aktualisiert!");
        refetch();
      } else {
        toast.error(data.error || "Upload fehlgeschlagen.");
      }
      setAvatarUploading(false);
    },
    onError: () => {
      toast.error("Profilbild-Upload fehlgeschlagen.");
      setAvatarUploading(false);
    },
  });

  const removeAvatarMutation = trpc.clientAuth.removeAvatar.useMutation({
    onSuccess: () => {
      toast.success("Profilbild entfernt.");
      refetch();
    },
    onError: () => {
      toast.error("Fehler beim Entfernen des Profilbilds.");
    },
  });

  const resetAnamnese = trpc.clientAuth.resetAnamnese.useMutation({
    onSuccess: () => {
      toast.success(
        "Fragebogen zurückgesetzt. Du kannst ihn jetzt neu ausfüllen."
      );
      refetch();
      // Redirect zum Onboarding nach kurzem Delay
      setTimeout(() => {
        window.location.href = "/app/onboarding";
      }, 1500);
    },
    onError: () => {
      toast.error("Fehler beim Zurücksetzen des Fragebogens.");
    },
  });

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !client) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte wähle eine Bilddatei (JPG, PNG, WebP).");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Das Bild darf maximal 5 MB groß sein.");
      return;
    }

    setAvatarUploading(true);

    // Resize image to max 400x400 before upload
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = 400;
        let w = img.width;
        let h = img.height;
        if (w > h) {
          if (w > maxSize) {
            h = Math.round((h * maxSize) / w);
            w = maxSize;
          }
        } else {
          if (h > maxSize) {
            w = Math.round((w * maxSize) / h);
            h = maxSize;
          }
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, w, h);
          const base64 = canvas.toDataURL("image/jpeg", 0.85).split(",")[1];
          uploadAvatarMutation.mutate({
            clientId: client.clientId,
            imageBase64: base64,
            mimeType: "image/jpeg",
          });
        } else {
          setAvatarUploading(false);
          toast.error("Bild konnte nicht verarbeitet werden.");
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  if (loading || !client) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#C9A96E]" />
      </div>
    );
  }

  const initials =
    `${client.firstName[0] || ""}${client.lastName[0] || ""}`.toUpperCase();

  return (
    <div className="space-y-8 max-w-2xl mx-auto animate-in fade-in duration-500">
      {/* Header with Avatar */}
      <div className="flex items-center gap-5">
        {/* Avatar */}
        <div className="relative group">
          <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#C9A96E]/20 shadow-sm">
            {client.avatarUrl ? (
              <img
                src={client.avatarUrl}
                alt={`${client.firstName} ${client.lastName}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#6b7f4e]/20 to-[#C9A96E]/20 flex items-center justify-center">
                <span className="text-xl font-semibold text-[#6b7f4e]">
                  {initials}
                </span>
              </div>
            )}
          </div>
          {/* Upload Overlay */}
          <label className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex items-center justify-center">
            {avatarUploading ? (
              <Loader2 className="w-5 h-5 animate-spin text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            ) : (
              <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarUpload}
              className="hidden"
              disabled={avatarUploading}
            />
          </label>
          {/* Remove button */}
          {client.avatarUrl && (
            <button
              onClick={() =>
                removeAvatarMutation.mutate({ clientId: client.clientId })
              }
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border border-[#2D2D2D]/10 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
              title="Profilbild entfernen"
            >
              <X className="w-3 h-3 text-[#2D2D2D]/50 hover:text-red-500" />
            </button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#2D2D2D] break-words">
            {client.firstName} {client.lastName}
          </h1>
          <p className="text-[#2D2D2D]/40 mt-1 text-sm">
            Verwalte deine Daten, Benachrichtigungen, Telegram-Verknüpfung und
            Abonnement
          </p>
          <p className="text-[10px] text-[#2D2D2D]/30 mt-1">
            Klicke auf das Bild, um dein Profilbild zu ändern
          </p>
        </div>
      </div>

      {/* Personal Info */}
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
          <User className="w-4 h-4 text-[#C9A96E]" />
          <h2 className="text-sm font-medium text-[#2D2D2D]">
            Persönliche Daten
          </h2>
        </div>
        <div className="divide-y divide-[#2D2D2D]/5">
          <div className="px-5 py-3.5 flex items-center justify-between gap-3">
            <span className="text-sm text-[#2D2D2D]/50 shrink-0">Name</span>
            <span className="text-sm text-[#2D2D2D] font-medium truncate">
              {client.firstName} {client.lastName}
            </span>
          </div>
          <div className="px-5 py-3.5 flex items-center justify-between gap-3">
            <span className="text-sm text-[#2D2D2D]/50 shrink-0">E-Mail</span>
            <span className="text-sm text-[#2D2D2D] truncate">{client.email}</span>
          </div>
          {/* Geburtsdatum — inline editierbar */}
          <BirthdateSection clientId={client.clientId} />
          <div className="px-5 py-3.5 flex items-center justify-between">
            <span className="text-sm text-[#2D2D2D]/50">Paket</span>
            <span className="text-sm text-[#C9A96E] font-medium bg-[#C9A96E]/5 px-2.5 py-0.5 rounded-full">
              AYU
            </span>
          </div>
          <CoachingStyleSection
            clientId={client.clientId}
            currentStyle={client.coachingStyle}
          />
          <div className="px-5 py-3.5 flex items-center justify-between">
            <span className="text-sm text-[#2D2D2D]/50">Anamnese</span>
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${
                  client.onboardingComplete
                    ? "text-[#C9A96E] bg-[#C9A96E]/5"
                    : "text-[#2D2D2D]/40 bg-[#2D2D2D]/5"
                }`}
              >
                {client.onboardingComplete ? "Ausgefüllt" : "Noch offen"}
              </span>
              {client.onboardingComplete && client.isAdmin && (
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Möchtest du den Fragebogen wirklich zurücksetzen und neu ausfüllen? Deine bisherigen Antworten werden gelöscht."
                      )
                    ) {
                      resetAnamnese.mutate({ clientId: client.clientId });
                    }
                  }}
                  className="text-xs text-[#B87A5E] hover:text-[#a06a4e] underline underline-offset-2 transition-colors"
                >
                  Neu ausfüllen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Betreuer — Deaktiviert / Demnächst verfügbar */}
      <div className="bg-[#f0ede8] rounded-2xl border border-[#2D2D2D]/5 overflow-hidden opacity-60">
        <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
          <Heart className="w-4 h-4 text-[#999]" />
          <h2 className="text-sm font-medium text-[#999]">Dein Betreuer</h2>
          <span className="ml-auto text-[10px] font-medium text-[#999] bg-[#e0ddd6] px-2 py-0.5 rounded-full">
            Demnächst
          </span>
        </div>
        <div className="p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#ddd]/30 flex items-center justify-center border-2 border-[#ccc]/30">
            <User className="w-6 h-6 text-[#bbb]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-[#999]">
              Noch kein Betreuer zugewiesen
            </p>
            <p className="text-xs text-[#999]/70 mt-0.5">
              Persönliche Einzelsitzungen mit einem Betreuer werden demnächst
              verfügbar sein.
            </p>
          </div>
        </div>
      </div>

      {/* Stimmenwahl */}
      <VoicePreferenceSection
        clientId={client.clientId}
        currentPreference={client.voicePreference ?? "annam"}
      />

      {/* Sprachauswahl */}
      <LanguageSection />

      {/* Telegram-Verknüpfung: AYU Coach */}
      <TelegramLinkSection clientId={client.clientId} />

      {/* Telegram-Verknüpfung: Community Bot */}
      <CommunityBotSection clientId={client.clientId} />

      {/* Benachrichtigungs-Einstellungen (Kanäle + Kategorien) */}
      <NotificationPreferencesSection clientId={client.clientId} />

      {/* Audio-Sessions Abo */}
      <SubscriptionSection clientId={client.clientId} email={client.email} />

      {/* Tutorial neu starten */}
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C9A96E]" />
            <h2 className="text-sm font-medium text-[#2D2D2D]">Tutorial</h2>
          </div>
          <RestartTourButton />
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#2D2D2D]/40" />
          <h2 className="text-sm font-medium text-[#2D2D2D]">Häufige Fragen</h2>
        </div>
        <div className="divide-y divide-[#2D2D2D]/5">
          {sharedConfig.faq.map((item, i) => (
            <details key={i} className="group">
              <summary className="px-5 py-3.5 text-sm text-[#2D2D2D] cursor-pointer hover:bg-[#2D2D2D]/[0.02] transition-colors list-none flex items-center justify-between">
                <span>{item.q}</span>
                <span className="text-[#2D2D2D]/20 group-open:rotate-45 transition-transform text-lg">
                  +
                </span>
              </summary>
              <div className="px-5 pb-3.5 text-sm text-[#2D2D2D]/50 leading-relaxed">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Emergency */}
      <div className="bg-red-50/50 rounded-2xl border border-red-100 p-5 text-center">
        <Phone className="w-5 h-5 text-red-300 mx-auto mb-2" />
        <p className="text-sm text-red-400 font-medium">Akute Krise?</p>
        <p className="text-xs text-red-300 mt-1">
          {sharedConfig.emergencyNote}
        </p>
      </div>

      {/* Version */}
      <p className="text-[10px] text-[#2D2D2D]/15 text-center pb-4">
        {sharedConfig.appName} v{sharedConfig.version}
      </p>
    </div>
  );
}
